import { useEffect, useState, useCallback } from 'react';
import api from '../../utils/api';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { CheckCircle2, Circle, RefreshCw } from 'lucide-react';
import { toast } from 'sonner';

/**
 * GoLiveChecklistWidget — surfaces the 3-step Go-Live status the kickoff
 * email describes. Reads GET /api/retailer/settings/go-live-checklist and
 * shows a green tick per completed check. When all three are done, a
 * "Request Go-Live" button appears (calls the existing retailer onboarding
 * go-live endpoint if present; otherwise informs the retailer to reach out).
 *
 * Cluster 6 P1.
 */
export default function GoLiveChecklistWidget() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [requesting, setRequesting] = useState(false);

  const refresh = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get('/retailer/settings/go-live-checklist');
      setData(res.data);
    } catch (e) {
      // Not a retailer or endpoint unavailable — silently hide
      setData(null);
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { refresh(); }, [refresh]);

  if (!data) return null;
  if (data.store_status === 'live') return null; // hide once retailer is live

  const requestGoLive = async () => {
    setRequesting(true);
    try {
      // Existing onboarding endpoint (path is best-effort; ignore 404 gracefully)
      await api.post('/retailer/onboarding/request-go-live');
      toast.success('Go-live request submitted — admin will review shortly.');
      refresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Please reach out to admin@dealshaq.com to go live.');
    } finally { setRequesting(false); }
  };

  return (
    <Card data-testid="go-live-checklist-widget">
      <CardHeader className="flex flex-row items-center justify-between">
        <div>
          <CardTitle className="text-base">Go-Live checklist</CardTitle>
          <p className="text-xs text-slate-500 mt-1">
            Complete these three items, then request go-live to open to consumers.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Badge variant="outline">
            Status: {data.store_status || 'unknown'}
          </Badge>
          <Button size="sm" variant="ghost" onClick={refresh} disabled={loading} data-testid="go-live-refresh">
            <RefreshCw className={`w-3 h-3 ${loading ? 'animate-spin' : ''}`} />
          </Button>
        </div>
      </CardHeader>
      <CardContent className="space-y-2">
        {data.checks.map((c) => (
          <div key={c.id} className="flex items-start gap-3" data-testid={`go-live-check-${c.id}`}>
            {c.complete
              ? <CheckCircle2 className="w-5 h-5 text-emerald-500 shrink-0 mt-0.5" />
              : <Circle className="w-5 h-5 text-slate-300 shrink-0 mt-0.5" />}
            <div className="text-sm">
              <div className={c.complete ? 'text-slate-500 line-through' : 'text-slate-900'}>
                {c.label}
              </div>
              {c.progress && (
                <div className="text-xs text-slate-500 mt-0.5">
                  {c.progress.current} / {c.progress.required}
                </div>
              )}
            </div>
          </div>
        ))}
        {data.can_request_go_live && (
          <div className="pt-3">
            <Button
              onClick={requestGoLive}
              disabled={requesting}
              data-testid="request-go-live-btn"
              className="w-full"
            >
              {requesting ? 'Submitting…' : 'Request Go-Live'}
            </Button>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
